import { usesUsGradingScale } from "./gradingUtils";

/** Minimum class average (%) to pass the term. */
export const CLASS_PASS_AVERAGE_MARK = 50;

/** Pupils need marks in at least this many subjects before pass/fail applies. */
export const CLASS_PASS_MIN_SUBJECTS = 6;

/** Individual subject floor for the “at most 3 below 40%” secondary rule. */
export const CLASS_SUBJECT_PASS_MARK = 40;

/** Max subjects allowed below CLASS_SUBJECT_PASS_MARK (senior secondary). */
export const CLASS_MAX_SUBJECTS_BELOW_MIN = 3;

export function subjectsWithMarks(subjects) {
    return (subjects || []).filter(
        (s) => s.marksObtained != null && s.marksObtained !== "" && !Number.isNaN(Number(s.marksObtained)),
    );
}

export function computeSubjectAverage(subjects) {
    const rows = subjectsWithMarks(subjects);
    if (!rows.length) return null;
    const total = rows.reduce((sum, s) => sum + Number(s.marksObtained), 0);
    return Number((total / rows.length).toFixed(2));
}

export function countSubjectsBelow(subjects, minMark = CLASS_SUBJECT_PASS_MARK) {
    return subjectsWithMarks(subjects).filter((s) => Number(s.marksObtained) < minMark).length;
}

/** Senior secondary (Form 3–4 MSCE) uses the extra “≤3 subjects below 40%” rule. */
export function usesSecondarySubjectFloor(educationLevel, levelCode, sclassName) {
    const level = String(educationLevel || "").trim();
    if (level === "Primary") return false;
    if (usesUsGradingScale(level, levelCode, sclassName)) return false;
    return level === "Secondary";
}

/**
 * Term pass for class results display.
 * - Average of subject marks ≥ 50%
 * - Marks required in min(6, subjects offered in this class)
 * - Form 3–4: at most 3 subjects below 40%
 */
export function evaluateTermClassPass(
    subjects,
    educationLevel,
    levelCode,
    sclassName,
    subjectsOfferedCount = CLASS_PASS_MIN_SUBJECTS,
) {
    const rows = subjectsWithMarks(subjects);
    const requiredCount = Math.min(
        CLASS_PASS_MIN_SUBJECTS,
        Math.max(1, Number(subjectsOfferedCount) || CLASS_PASS_MIN_SUBJECTS),
    );
    const average = computeSubjectAverage(rows);
    const subjectsBelowMin = countSubjectsBelow(rows);

    if (rows.length < requiredCount) {
        return {
            passed: null,
            average,
            subjectCount: rows.length,
            requiredCount,
            subjectsBelowMin,
            incomplete: true,
            reason: `${rows.length}/${requiredCount} subjects`,
        };
    }

    if (average == null || average < CLASS_PASS_AVERAGE_MARK) {
        return {
            passed: false,
            average,
            subjectCount: rows.length,
            requiredCount,
            subjectsBelowMin,
            incomplete: false,
            reason: `Avg ${average.toFixed(1)}% (need ${CLASS_PASS_AVERAGE_MARK}%)`,
        };
    }

    if (
        usesSecondarySubjectFloor(educationLevel, levelCode, sclassName) &&
        subjectsBelowMin > CLASS_MAX_SUBJECTS_BELOW_MIN
    ) {
        return {
            passed: false,
            average,
            subjectCount: rows.length,
            requiredCount,
            subjectsBelowMin,
            incomplete: false,
            reason: `${subjectsBelowMin} below ${CLASS_SUBJECT_PASS_MARK}%`,
        };
    }

    return {
        passed: true,
        average,
        subjectCount: rows.length,
        requiredCount,
        subjectsBelowMin,
        incomplete: false,
        reason: `Avg ${average.toFixed(1)}%`,
    };
}

export function classPassRuleSummary(educationLevel, levelCode, sclassName) {
    const base = `Pass: average ≥ ${CLASS_PASS_AVERAGE_MARK}% across at least ${CLASS_PASS_MIN_SUBJECTS} subjects (or all subjects if the class has fewer).`;
    if (usesSecondarySubjectFloor(educationLevel, levelCode, sclassName)) {
        return `${base} Form 3–4: at most ${CLASS_MAX_SUBJECTS_BELOW_MIN} subjects below ${CLASS_SUBJECT_PASS_MARK}%.`;
    }
    return base;
}
